/* eslint eqeqeq: "off", curly: "error", @typescript-eslint/no-unused-vars: "off", react-hooks/exhaustive-deps: "off" */
import { useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { useThemeProvider, Card, FlexGrid, Typography, Button, Skeleton } from "@albo-ui/core";
import { Checkbox, Chip, Divider, MenuItem, Select, InputLabel, FormControl, ListItemText, Stack, TextField } from '@mui/material';
import { AccountBalance, PlayArrowOutlined, MoreHoriz, VisibilityOutlined, FileDownloadOutlined, AttachMoneyOutlined } from '@mui/icons-material';
import RefreshIcon from "@mui/icons-material/Refresh";
import { CustomMenu } from '../../elements';
import { strategyOptions } from './strategies';
import { useAfbFeeSimulation } from './hooks/useAfbFeeSimulation';
import { FeeConfirmModal } from './FeeConfirmModal';
import { FeeDetailModal } from './FeeDetailModal';
import { customStylesCompany } from '../../../util/TableStyles';
import { noDataComponent } from '../../../util/DataObject';
import moment from 'moment';

const currencyFmt = (v: number) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v ?? 0);

/* Generates the last 12 months as { value: 'YYYY-MM', label: 'Mes YYYY' } */
const generatePeriodOptions = () => {
    const options: { value: string; label: string }[] = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const label = moment(d).format('MMMM YYYY');
        options.push({ value, label });
    }
    return options;
};

const periodOptions = generatePeriodOptions();

export type SimulationRecord = {
    uuid: string;
    cdate: number;
    end_time: number;
    period: string;
    segments_to_process: string[];
    segments_to_process_str: string[];
    processed_customers: number;
    failed_customers: number;
    total_customers: number;
    total_fee: number;
    rows: any[];
    is_simulated: boolean;
    status: string;
    totals: any;
    metadata?: {
        file_uuid?: string;
        [key: string]: any;
    };
};

export const AfbFeeSimulation = ({ changeAlert, changeLoader }: any) => {
    const { theme } = useThemeProvider();

    const {
        selectedPeriod, setSelectedPeriod,
        segments, selectedSegments, setSelectedSegments, toggleSegment,
        simulationRecords,
        loading,
        confirmOpen, pendingRecord,
        config,
        canSimulate,
        canSingle,
        singleCustomerUuid, setSingleCustomerUuid,
        singleStrategy, setSingleStrategy,
        handleSingleFee,
        handleSimulate,
        handleDownloadExcel,
        openConfirmModal, closeConfirmModal, handleRealCharge, handleRefresh, handleExecutionDetails,
        detailOpen, detailData, detailLoading, closeExecutionDetails, handleDetailPageChange
    } = useAfbFeeSimulation(changeAlert, changeLoader);

    const hasAction = (action: string) => {
        return config?.actions?.indexOf(action) > -1;
    };
    
    useEffect(() => {
        changeLoader(loading);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    /* Opciones del menú de acciones por fila */
    const buildRowOptions = (r: SimulationRecord) => {
        const options: any[] = [
            { key: 'detail', label: 'Ver detalle', icon: <VisibilityOutlined fontSize="small" /> },
        ];
        const expired = moment().diff(moment(r.end_time), 'hours') >= 24;
        const hasReportFile = !!r.uuid && !!r.metadata?.file_uuid;
        if (hasAction('afbFeeDownload') && hasReportFile && r.status !== 'IN_PROGRESS' && r.status !== 'FAILED') {
            options.push({ key: 'download', label: 'Descargar Excel', icon: <FileDownloadOutlined fontSize="small" /> });
        }
        if (r.is_simulated) {
            if (expired) {
                options.push({ key: 'charge', label: 'Cobrar Real (simulación expirada >24h)', icon: <AttachMoneyOutlined fontSize="small" />, disabled: true });
            } else if (!loading && r.status !== 'IN_PROGRESS' && r.status !== 'FAILED') {
                options.push({ key: 'charge', label: 'Cobrar Real', icon: <AttachMoneyOutlined fontSize="small" /> });
            }
        }
        return options;
    };

    const handleRowAction = (row: SimulationRecord, key: string) => {
        if (key === 'detail') { handleExecutionDetails(row); }
        if (key === 'download') { handleDownloadExcel(row); }
        if (key === 'charge') { openConfirmModal(row); }
    };

    /* Build history-style DataTable columns */
    const historyColumns = [
        {
            name: 'Fecha de simulación',
            selector: (r: SimulationRecord) => r.cdate,
            sortable: true,
            cell: (r: SimulationRecord) => (
                <span>{moment(r.cdate).format('DD/MM/YYYY HH:mm')}</span>
            ),
            grow: 2,
        },
        {
            name: 'Segmentos simulados',
            cell: (r: SimulationRecord) => (
                r.segments_to_process_str && <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {r.segments_to_process_str?.map((segment: string) => (
                        <Chip
                            key={segment}
                            label={segment}
                            size="medium"
                        />
                    ))}
                </Stack>
            ),
            sortable: true, wrap: true, grow: 2.5
        },
        {
            name: 'Clientes',
            selector: (r: SimulationRecord) => r.processed_customers,
            sortable: true,
            right: true,
            cell: (r: SimulationRecord) => <span>{r.processed_customers?.toLocaleString('es-MX')}</span>,
        },
        {
            name: 'Total a cobrar',
            selector: (r: SimulationRecord) => r.totals?.total_fee_amount,
            sortable: true,
            cell: (r: SimulationRecord) => <span style={{ fontWeight: 800 }}>{currencyFmt(r.totals?.total_fee_amount)}</span>,
            grow: 1.5,
            right: true,
        },
        {
            name: 'Estado',
            selector: (r: SimulationRecord) => r.status,
            cell: (r: SimulationRecord) => {
                const isFailed = r.status === 'FAILED';
                const isCharged = r.status === 'COMPLETED' && !r.is_simulated;
                const isSimulated = r.status === 'COMPLETED' && r.is_simulated;

                let label = 'En proceso';
                let dotColor = '#6a4fbd';
                let bgColor = '#efeafb';
                let textColor = '#6a4fbd';

                if (isSimulated) {
                    label = 'Simulado';
                    dotColor = '#c8922a';
                    bgColor = '#fdf3e0';
                    textColor = '#c8922a';
                } else if (isCharged) {
                    label = 'Cobrado';
                    dotColor = '#2b9080';
                    bgColor = '#e0f2f1';
                    textColor = '#2b9080';
                } else if (isFailed) {
                    label = 'Fallido';
                    dotColor = '#d32f2f';
                    bgColor = '#ffebee';
                    textColor = '#d32f2f';
                }

                const exitosos = r.processed_customers ?? 0;
                const fallidos = r.failed_customers ?? 0;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                        <Chip
                            icon={
                                <span style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: dotColor,
                                    display: 'inline-block',
                                    marginLeft: 8,
                                }} />
                            }
                            label={label}
                            size="medium"
                            sx={{
                                backgroundColor: bgColor,
                                color: textColor,
                                fontWeight: 600,
                                fontSize: '0.85rem',
                                borderRadius: '20px',
                                px: 1,
                                maxWidth: "fit-content",
                            }}
                        />
                        {isCharged && (
                            <Typography
                                variant="smallTitle"
                                style={{
                                    fontSize: '0.75rem',
                                    color: '#999',
                                    marginTop: 4,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {exitosos.toLocaleString('es-MX')} exitosos · {fallidos.toLocaleString('es-MX')} fallidos
                            </Typography>
                        )}
                    </div>
                );
            },
            grow: 1.5,
        },
        {
            name: 'Acciones',
            cell: (r: SimulationRecord) => (
                <CustomMenu
                    row={r}
                    options={buildRowOptions(r)}
                    action={handleRowAction}
                    icon={<MoreHoriz />}
                />
            ),
            grow: 1.5, ignoreRowClick: true,
        },
    ];

    return (
        <Card padding={4} flexDirection="column" fullWidth fullHeight alignItems="stretch" justifyContent="space-between" style={{ borderColor: theme.colors.manatee, marginBotton: 20 }}>
        {loading ? (
            <Skeleton width="100%" height="100px"/>
        ) : (
            /* ── Header ────────────────────────────────────────────────── */
            <><FlexGrid container fullWidth justify='space-between' style={{ display: 'flex', marginBottom: 25 }}>
                <FlexGrid item container direction="column">
                    <Typography variant='h2' style={{display: 'inline-flex', alignItems: 'center', fontSize: '1.2rem'}}>
                        <AccountBalance sx={{ marginRight: 1 }}/> Simulación y Aplicación de Fees Mensuales
                    </Typography>
                    <Typography variant="smallTitle" color='darkGray'>
                        Simula el cobro mensual por segmento sin afectar saldos y, al validar, ejecuta el cobro real.
                    </Typography>
                </FlexGrid>
            </FlexGrid>

            {/*  ── Filters Section ────────────────────────────────────────── */ }
            <FlexGrid container fullWidth style={{ marginTop: 20 }}>
                <FlexGrid container spacing={2} fullWidth justify="space-between" alignItems="flex-start">
                    {/* Period selector */}
                    <FlexGrid item style={{ width: '30%', minWidth: 200 }}>
                        <FormControl fullWidth size="small" sx={{ '& fieldset legend': { display: 'block', maxWidth: 'fit-content' } }}>
                            <InputLabel id="fee-period-select-label">Período</InputLabel>
                            <Select
                                labelId="fee-period-select-label"
                                id="fee-period-select"
                                value={selectedPeriod}
                                label="Período"
                                onChange={e => setSelectedPeriod(e.target.value)}
                            >
                                {periodOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FlexGrid>

                    {/* Segments select dropdown with checkboxes */}
                    <FlexGrid item style={{ flex: 1, minWidth: 300 }}>
                        {loading ? (
                            <Skeleton width="100%" height="40px" />
                        ) : (
                            <FormControl fullWidth size="small" >
                                <InputLabel id="seg-select-label">Segmentos *</InputLabel>
                                <Select
                                    labelId="seg-select-label"
                                    id="seg-select"
                                    multiple
                                    label="Segmentos *"
                                    value={selectedSegments}
                                    onChange={(e) => {
                                        const values = e.target.value as string[];
                                        setSelectedSegments(values);
                                    }}
                                    renderValue={(selected) => (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                            {(selected as string[]).map((value) => {
                                                const seg = segments.find(s => s.k === value);
                                                return (
                                                    <Chip key={value} label={seg ? seg.v : value} size="small" />
                                                );
                                            })}
                                        </div>
                                    )}
                                    MenuProps={{ style: { zIndex: 35001 } }}
                                >
                                    {segments.map((seg: any) => (
                                        <MenuItem key={seg.k} value={seg.k}>
                                            <Checkbox checked={selectedSegments.includes(seg.k)} size="small" />
                                            <ListItemText primary={seg.v} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FlexGrid>
                </FlexGrid>

                <FlexGrid container fullHeight justify="flex-end" alignItems="flex-end" style={{ width: '40%' }}>
                    <FlexGrid item>
                        <Button onClick={handleSimulate} disabled={!canSimulate || loading} size="normal" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}} ><PlayArrowOutlined sx={{ mr: 1 }} /> Simular Cobro</Button>
                    </FlexGrid>
                </FlexGrid>
            </FlexGrid>

            {/* ── Single Fee Section ────────────────────────────────────── */}
            <Divider sx={{ mt: 4, mb: 3 }} />
            <FlexGrid container fullWidth style={{ marginBottom: 15 }}>
                <FlexGrid item container direction="column">
                    <Typography variant='h2' style={{display: 'inline-flex', alignItems: 'center', fontSize: '1.2rem'}}>
                        <AccountBalance sx={{ marginRight: 1 }}/> Simulación individual
                    </Typography>
                    <Typography variant="smallTitle" color='darkGray'>
                        Simula o aplica el fee de un cliente según la estrategia seleccionada.
                    </Typography>
                </FlexGrid>
            </FlexGrid>
            <FlexGrid container fullWidth >
                <FlexGrid container spacing={2} fullWidth justify="space-between" alignItems="flex-start">
                    <TextField
                        fullWidth
                        size="small"
                        label="Customer UUID *"
                        value={singleCustomerUuid}
                        onChange={e => setSingleCustomerUuid(e.target.value)}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel id="fee-strategy-select-label">Estrategia *</InputLabel>
                        <Select
                            labelId="fee-strategy-select-label"
                            id="fee-strategy-select"
                            value={singleStrategy}
                            label="Estrategia *"
                            onChange={e => setSingleStrategy(e.target.value)}
                        >
                            {strategyOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FlexGrid>
                {/* <FlexGrid item style={{ flex: 1, minWidth: 240 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="fee-strategy-select-label">Estrategia *</InputLabel>
                        <Select
                            labelId="fee-strategy-select-label"
                            id="fee-strategy-select"
                            value={singleStrategy}
                            label="Estrategia *"
                            onChange={e => setSingleStrategy(e.target.value)}
                        >
                            {strategyOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </FlexGrid> */}
                <FlexGrid container fullHeight justify="flex-end" alignItems="flex-end" style={{ width: '40%' }}>
                    <Button onClick={() => handleSingleFee(true)} disabled={!canSingle || loading} size="normal" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}><PlayArrowOutlined sx={{ mr: 1 }} />Simular cobro</Button>
                </FlexGrid>
            </FlexGrid>

            {/* ── Results History Table ─────────────────────────────────── */}
            {simulationRecords.length > 0 && (
                <>
                    <Divider sx={{ mt: 4, mb: 3 }} />
                    <FlexGrid container fullWidth justify="space-between" style={{ marginBottom: 25, display: "flex" }}>
                        <FlexGrid item justify="space-between" fullWidth>
                            <Typography variant="h2" style={{display: "flex", alignItems: "center", fontSize: "1.2rem"}}>
                                <RefreshIcon cursor="pointer" onClick={handleRefresh} sx={{padding: 1, borderRadius: 2, boxSizing: "content-box", color: "white", backgroundColor: "#8bc179", height: "-webkit-fill-available", marginRight: 1 }}/>Historial de simulaciones
                            </Typography>
                        </FlexGrid>
                    </FlexGrid>
                    <DataTable
                        columns={historyColumns as any}
                        data={simulationRecords}
                        customStyles={customStylesCompany}
                        noDataComponent={noDataComponent}
                        pagination
                    />
                </>
            )}

            {/* ── Detail Modal ───────────────────────────────────────────── */}
            <FeeDetailModal
                open={detailOpen}
                data={detailData}
                loading={detailLoading}
                onClose={closeExecutionDetails}
                onPageChange={handleDetailPageChange}
            />

            {/* ── Confirm Modal ──────────────────────────────────────────── */}
            <FeeConfirmModal
                open={confirmOpen}
                summary={pendingRecord ? { total_customers: pendingRecord.processed_customers, total_fee: pendingRecord.totals?.total_fee_amount } : null}
                period={selectedPeriod}
                segments={pendingRecord?.segments_to_process_str}
                onConfirm={handleRealCharge}
                onClose={closeConfirmModal}
            />
        </>
        )}
        </Card>
    );
};
